import { useCallback, useMemo, useState, type FormEvent } from 'react';
import { LifeBuoy } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { SidebarMenuButton, SidebarMenuItem } from '@/components/ui/sidebar';
import { Textarea } from '@/components/ui/textarea';
import { csrfFetch } from '@/lib/csrf';

const MIN_MESSAGE_LENGTH = 100;

export default function TherapistSupportMenuItem() {
  const [open, setOpen] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const trimmedLength = useMemo(() => message.trim().length, [message]);
  const remaining = Math.max(0, MIN_MESSAGE_LENGTH - trimmedLength);

  const handleOpenChange = useCallback((nextOpen: boolean) => {
    setOpen(nextOpen);
    if (!nextOpen) {
      setMessage('');
      setError(null);
      setIsSubmitting(false);
      setIsSubmitted(false);
    }
  }, []);

  const handleSubmit = useCallback(
    async (event: FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      if (isSubmitting) return;
      if (trimmedLength < MIN_MESSAGE_LENGTH) {
        setError('Raccontaci qualche dettaglio in più: servono almeno 100 caratteri.');
        return;
      }

      setIsSubmitting(true);
      setError(null);

      try {
        const response = await csrfFetch('/therapists/support_requests', {
          method: 'POST',
          body: JSON.stringify({ support_request: { message } }),
        });

        if (!response.ok) {
          const payload = await response.json().catch(() => ({}));
          setError(payload?.error || 'Non siamo riusciti a inviare la richiesta. Riprova tra poco.');
          setIsSubmitting(false);
          return;
        }

        setIsSubmitted(true);
        setIsSubmitting(false);
      } catch {
        setError('Qualcosa è andato storto mentre inviavamo la richiesta. Riprova tra poco.');
        setIsSubmitting(false);
      }
    },
    [isSubmitting, message, trimmedLength],
  );

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <SidebarMenuItem>
        <DialogTrigger asChild>
          <SidebarMenuButton>
            <LifeBuoy className="size-4" />
            <span>Assistenza</span>
          </SidebarMenuButton>
        </DialogTrigger>
      </SidebarMenuItem>
      <DialogContent className="sm:max-w-[520px]">
        {isSubmitted ? (
          <div className="space-y-5">
            <DialogHeader>
              <DialogTitle>Richiesta inviata</DialogTitle>
              <DialogDescription>
                Grazie per averci scritto. Il team di supporto è già stato avvisato e ti risponderà
                rapidamente. Se nel frattempo hai urgenza, siamo sempre qui per te.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button type="button" onClick={() => handleOpenChange(false)}>
                Chiudi
              </Button>
            </DialogFooter>
          </div>
        ) : (
          <form className="space-y-4" onSubmit={handleSubmit}>
            <DialogHeader>
              <DialogTitle>Serve una mano?</DialogTitle>
              <DialogDescription>
                Raccontaci cosa succede e ti rispondiamo il prima possibile. Siamo qui per supportarti
                con attenzione e rapidità.
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-2">
              <Label htmlFor="support-message">Messaggio</Label>
              <Textarea
                id="support-message"
                placeholder="Descrivi la richiesta in modo dettagliato..."
                value={message}
                onChange={(event) => setMessage(event.target.value)}
                rows={6}
              />
              <p className="text-xs text-muted-foreground">
                {remaining > 0
                  ? `Aggiungi ancora ${remaining} caratteri per inviare la richiesta.`
                  : 'Grazie! Puoi inviare la richiesta quando vuoi.'}
              </p>
              {error && <p className="text-xs text-destructive">{error}</p>}
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => handleOpenChange(false)}>
                Annulla
              </Button>
              <Button type="submit" isLoading={isSubmitting} disabled={trimmedLength < MIN_MESSAGE_LENGTH}>
                Invia richiesta
              </Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
